import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

import { useTranslation } from "react-i18next";

import { QueryObserverResult } from "@tanstack/react-query";

import { HouseholdData } from "@/api/rest/models/household";
import { Room } from "@/api/rest/models/room";
import { createRoom, deleteRoom, updateRoom } from "@/api/rest/services/roomService";
import ManagedPopup from "@/components/common/ManagedPopup.tsx";

interface EditingRoom {
  id: number;
  name: string;
  floor: number;
}

export default function RoomEditPopup({
  householdData,
  updateHousehold,
}: {
  householdData: HouseholdData;
  updateHousehold: () => Promise<QueryObserverResult>;
}) {
  const [newRoomName, setNewRoomName] = useState<string>("");
  const [newRoomFloor, setNewRoomFloor] = useState<number>(1);
  const [editingRoom, setEditingRoom] = useState<EditingRoom | null>(null);
  const { t } = useTranslation();

  const removeRoom = async (roomId: number) => {
    await deleteRoom(householdData.household.id, roomId);
    await updateHousehold();
  };

  const addRoom = async () => {
    if (!newRoomName.trim()) {
      return;
    }
    await createRoom(householdData.household.id, newRoomName.trim(), newRoomFloor);
    setNewRoomName("");
    setNewRoomFloor(1);
    await updateHousehold();
  };

  const startEditing = (room: Room) => {
    setEditingRoom({
      id: room.id,
      name: room.name,
      floor: room.floor,
    });
  };

  const cancelEditing = () => {
    setEditingRoom(null);
  };

  const saveRoom = async () => {
    if (!editingRoom || !editingRoom.name.trim()) {
      return;
    }
    const originalRoom = householdData.rooms.find((r) => r.id === editingRoom.id);
    const nameChanged = originalRoom?.name !== editingRoom.name.trim();
    const floorChanged = originalRoom?.floor !== editingRoom.floor;

    if (nameChanged || floorChanged) {
      await updateRoom(
        householdData.household.id,
        editingRoom.id,
        nameChanged ? editingRoom.name.trim() : undefined,
        floorChanged ? editingRoom.floor : undefined,
      );
      await updateHousehold();
    }
    setEditingRoom(null);
  };

  // Get unique floors and sort them
  const floors = [...new Set(householdData.rooms.map((r) => r.floor))].sort((a, b) => b - a);
  const roomsByFloor = floors.map((floor) => ({
    floor,
    rooms: householdData.rooms.filter((r) => r.floor === floor),
  }));

  // Floor options for select (show existing floors + option to add new, starting from 0 for outdoor)
  const maxFloor = Math.max(...householdData.rooms.map((r) => r.floor), 1);
  const floorOptions = Array.from({ length: maxFloor + 3 }, (_, i) => i);

  const getFloorLabel = (floor: number) => {
    return floor === 0 ? t("room.outdoor") : `${t("room.floor")} ${floor}`;
  };

  return (
    <ManagedPopup
      contentSize="data"
      trigger={t("room.change")}
      title={t("room.change")}
      triggerClassname="pl-4 md:invisible group-hover:visible font-semibold text-sm text-gray-500 hover:text-dobu-orange-focus cursor-pointer"
    >
      <div className="p-4 flex flex-col overflow-y-auto max-h-[70vh] min-w-[400px]">
        {/* Room list grouped by floor */}
        {roomsByFloor.map(({ floor, rooms }) => (
          <div key={floor} className="mb-4">
            <div className="text-sm font-medium text-gray-500 mb-2 border-b border-gray-200 pb-1">
              {getFloorLabel(floor)}
            </div>
            <div className="space-y-1">
              {rooms.map((room) => (
                <div key={room.id}>
                  {editingRoom?.id === room.id ? (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex flex-col gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t("room.name")}</label>
                          <input
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dobu-orange focus:border-transparent"
                            type="text"
                            value={editingRoom.name}
                            onChange={(e) => setEditingRoom({ ...editingRoom, name: e.target.value })}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t("room.floor")}</label>
                          <select
                            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dobu-orange focus:border-transparent bg-white"
                            value={editingRoom.floor}
                            onChange={(e) => setEditingRoom({ ...editingRoom, floor: parseInt(e.target.value) })}
                          >
                            {floorOptions.map((f) => (
                              <option key={f} value={f}>
                                {getFloorLabel(f)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <button
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors"
                            onClick={cancelEditing}
                          >
                            {t("common.cancel")}
                          </button>
                          <button
                            className="px-3 py-1.5 text-sm bg-dobu-orange text-white hover:bg-dobu-orange-focus rounded-md transition-colors"
                            onClick={saveRoom}
                          >
                            {t("room.save")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded-lg group transition-colors">
                      <span className="font-medium text-gray-900">{room.name}</span>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-1.5 text-gray-400 hover:text-dobu-orange hover:bg-gray-100 rounded-md transition-colors"
                          onClick={() => startEditing(room)}
                          title={t("room.rename")}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          onClick={() => removeRoom(room.id)}
                          title={t("room.delete")}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Add new room section */}
        <div className="border-t border-gray-200 pt-4 mt-2">
          <div className="text-sm font-medium text-gray-700 mb-3">{t("room.addNew")}</div>
          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <div className="flex-grow">
                <input
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dobu-orange focus:border-transparent"
                  name="roomName"
                  type="text"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                  placeholder={t("room.newRoomName")}
                  onKeyDown={(e) => e.key === "Enter" && addRoom()}
                />
              </div>
              <select
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dobu-orange focus:border-transparent bg-white min-w-[100px]"
                value={newRoomFloor}
                onChange={(e) => setNewRoomFloor(parseInt(e.target.value))}
              >
                {floorOptions.map((f) => (
                  <option key={f} value={f}>
                    {getFloorLabel(f)}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="w-full py-2 px-4 bg-dobu-orange text-white rounded-md hover:bg-dobu-orange-focus transition-colors text-sm font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={addRoom}
              disabled={!newRoomName.trim()}
            >
              {t("room.add")}
            </button>
          </div>
        </div>
      </div>
    </ManagedPopup>
  );
}
